/**
 * src/pages/LandingPage.tsx — Phase 16 ACCESS-01
 *
 * Anonymous landing page with value proposition and inline magic-link auth form.
 * Shown by <RequireAuth> when authLoading===false && !userId.
 *
 * Sub-components extracted per CLAUDE.md rule 5 (200-line limit, IN-01):
 *   - BenefitList: benefit bullets
 *   - AuthFormCard: magic-link form + sent confirmation (manages own email/step state)
 */

import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Logo } from '@/shared/ui/Logo';
import { LanguageSelector } from '@/shared/ui/LanguageSelector';
import { useReducedMotion } from '@/shared/lib/hooks/useReducedMotion';
import { BenefitList } from '@/pages/landing/BenefitList';
import { AuthFormCard } from '@/pages/landing/AuthFormCard';
import { PricingGrid } from '@/pages/landing/PricingGrid';

const LandingPage: FC = () => {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const duration = reduced ? 0 : 0.4;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 overflow-x-hidden">
      {/* Background decorations — exact copy from StartPage */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-100/50 dark:bg-blue-900/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute top-1/2 -right-24 w-80 h-80 bg-purple-100/50 dark:bg-purple-900/10 rounded-full blur-3xl opacity-50" />
      </div>

      {/* Header */}
      <header className="relative h-20 flex items-center justify-between px-6 md:px-12 z-10">
        <Logo className="h-9 w-auto" />
        {/* Right: LanguageSelector only — user is anonymous */}
        <div className="flex items-center gap-4">
          <LanguageSelector />
        </div>
      </header>

      {/* Main: single centered column */}
      <main
        className="relative z-10 max-w-2xl mx-auto px-6 pt-12 pb-24 flex flex-col gap-12"
        aria-label={t('landing.main_label', 'Login page')}
      >
        <motion.div
          className="flex flex-col gap-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration }}
        >
          {/* Hero section */}
          <section className="flex flex-col gap-4 text-center">
            <h1 className="text-heading sm:text-display text-slate-900 dark:text-slate-100 font-black tracking-tight">
              {t('landing.headline', 'Smart geometry — precise estimate')}
            </h1>
            <p className="text-subhead text-slate-500 dark:text-slate-400 font-medium">
              {t('landing.subheadline', 'Your digital site foreman in your pocket')}
            </p>
          </section>

          {/* Benefit bullets — extracted for line-limit compliance (IN-01, CLAUDE.md rule 5) */}
          <BenefitList />

          {/* Auth form card — extracted for line-limit compliance (IN-01, CLAUDE.md rule 5) */}
          <motion.div
            id="landing-auth-form"
            className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800 shadow-card p-6 flex flex-col gap-4"
            whileHover={reduced ? undefined : { scale: 1.01 }}
          >
            <AuthFormCard />
          </motion.div>

          {/* Pricing grid — replaces the former price-hint paragraph (Phase 38) */}
          <PricingGrid />
        </motion.div>
      </main>
    </div>
  );
};

export default LandingPage;
